import { combineLatest, Observable } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Tag } from 'src/app/features/tag/tag'
import { tagActions } from 'src/app/features/tag/tag.actions'
import { tagSelectors } from 'src/app/features/tag/tag.selectors'
import { SelectionLengthValidator } from 'src/app/shared/validators/selection-length.validator'
import { environment } from 'src/environments/environment'

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'

import { configurationOptionSelectors } from '../../configuration-option.selectors'
import { ConfigOptionModule } from '../../configuration-option'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { scrollToFirstInvalidControl } from 'src/app/shared/until'
import { NotificationService } from 'src/app/core/notification.service'

@Component({
  selector: 'configuration-option-primary-tag-filter',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionPrimaryTagFilterCardComponent
  implements OnInit
{
  @Output() onSubmit = new EventEmitter<any>()
  @Input() formValues$: Observable<any>
  @Input() isCollapsed: any = false
  @Input() discoveryAreaConfigHasValue$: Observable<boolean>
  public form: UntypedFormGroup
  public isLoading$: Observable<boolean>
  public tags$: Observable<Tag[]>
  public selectedLanguage$: Observable<string>
  public maxNumberOfExampleTags: number = 3
  private configOptionModule: ConfigOptionModule =
    environment.moduleConfigs?.configOptionModule
  public isShopEnabled: boolean = true
  public accessRights = AccessRights
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return (acc[curr] = true), acc
      else return acc
    },
    {}
  )

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private notificationService: NotificationService
  ) {}

  get configSetsFormArray() {
    return this.form?.controls?.configSets as UntypedFormArray
  }

  get configSetsCoursesFormArray() {
    return this.form?.controls?.configSetsCourses as UntypedFormArray
  }

  get configSetsSubscriptionsFormArray() {
    return this.form?.controls?.configSetsSubscriptions as UntypedFormArray
  }

  get configSetsRentalsFormArray() {
    return this.form?.controls?.configSetsRentals as UntypedFormArray
  }

  ngOnInit() {
    this.form = this.fb.group({
      configSets: this.fb.array([]),
      configSetsCourses: this.fb.array([]),
      configSetsSubscriptions: this.fb.array([]),
      configSetsRentals: this.fb.array([]),
      exampleTagIds: [
        [],
        [SelectionLengthValidator(this.maxNumberOfExampleTags)]
      ],
      exampleTagIdsCourses: [
        [],
        [SelectionLengthValidator(this.maxNumberOfExampleTags)]
      ],
      exampleTagIdsSubscriptions: [
        [],
        [SelectionLengthValidator(this.maxNumberOfExampleTags)]
      ],
      exampleTagIdsRentals: [
        [],
        [SelectionLengthValidator(this.maxNumberOfExampleTags)]
      ]
    })

    this.isLoading$ = this.store.select(
      configurationOptionSelectors.selectIsLoading
    )

    this.tags$ = combineLatest([
      this.store.select(tagSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ]).pipe(
      map(([tags, language]) => {
        return tags.map((tag) => {
          return {
            ...tag,
            description: tag[`description_${language}`]
          }
        })
      })
    )

    this.store.dispatch(tagActions.fetchMany({}))

    this.formValues$
      .pipe(
        filter(
          (formValues) =>
            // only require primary_tag_filter_config to exist as other fields could be missing and should be getting
            // the default empty value if they are in fact missing
            formValues?.primary_tag_filter_config !== undefined &&
            formValues?.primary_tag_filter_config !== null
        ),
        take(1)
      )
      .subscribe((formValues) => {
        const configSets = JSON.parse(
          formValues?.primary_tag_filter_config || '[]'
        )

        const configSetsCourses = JSON.parse(
          formValues?.primary_tag_filter_config_courses || '[]'
        )

        const configSetsSubscriptions = JSON.parse(
          formValues?.primary_tag_filter_config_subscriptions || '[]'
        )

        const configSetsRentals = JSON.parse(
          formValues?.primary_tag_filter_config_rentals || '[]'
        )

        configSets?.forEach(() => {
          if (this.configSetsFormArray?.length < configSets?.length) {
            this.addConfigSetRow(this.configSetsFormArray)
          }
        })

        configSetsCourses?.forEach(() => {
          if (
            this.configSetsCoursesFormArray?.length < configSetsCourses?.length
          ) {
            this.addConfigSetRow(this.configSetsCoursesFormArray)
          }
        })

        configSetsSubscriptions?.forEach(() => {
          if (
            this.configSetsSubscriptionsFormArray?.length <
            configSetsSubscriptions?.length
          ) {
            this.addConfigSetRow(this.configSetsSubscriptionsFormArray)
          }
        })

        configSetsRentals?.forEach(() => {
          if (
            this.configSetsRentalsFormArray?.length < configSetsRentals?.length
          ) {
            this.addConfigSetRow(this.configSetsRentalsFormArray)
          }
        })

        const exampleTagIds = JSON.parse(
          formValues?.tagfilter_example_ids || '[]'
        )
        const exampleTagIdsCourses = JSON.parse(
          formValues?.tagfilter_example_ids_courses || '[]'
        )
        const exampleTagIdsSubscriptions = JSON.parse(
          formValues?.tagfilter_example_ids_subscriptions || '[]'
        )

        const exampleTagIdsRentals = JSON.parse(
          formValues?.tagfilter_example_ids_rentals || '[]'
        )

        this.form?.patchValue({
          configSets,
          configSetsCourses,
          configSetsSubscriptions,
          configSetsRentals,
          exampleTagIds,
          exampleTagIdsCourses,
          exampleTagIdsSubscriptions,
          exampleTagIdsRentals
        })
      })
  }

  public getConfigSetRow() {
    return this.fb.group({
      ordinal: [null, [Validators.required]],
      tagLevel: [null, [Validators.required]],
      activeState: [false],
      title_en: [null, [Validators.required]],
      title_nl: [
        [null, this.availableLanguages['nl'] ? [Validators.required] : []]
      ],
      title_de: [
        null,
        this.availableLanguages['de'] ? [Validators.required] : []
      ],
      title_fr: [
        null,
        this.availableLanguages['fr'] ? [Validators.required] : []
      ],
      title_es: [
        null,
        this.availableLanguages['es'] ? [Validators.required] : []
      ]
    })
  }

  public addConfigSetRow(formArray: UntypedFormArray): void {
    formArray.push(this.getConfigSetRow())
  }

  public deleteConfigSetRow(formArray: UntypedFormArray, index: number): void {
    formArray.removeAt(index)
  }

  public onFormSubmit(): void {
    // prevent saving if form hasnt been modified to avoid data loss
    if (this.form.pristine) {
      this.notificationService.emit(
        'configurationOptionModule.noChangesToSave',
        'warning'
      )
      return
    }

    if (this.form.invalid) {
      this.markGroupDirty(this.form)

      scrollToFirstInvalidControl(this.elementRef)
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    const exampleIdsArray = this.form.controls.exampleTagIds.value.sort()
    const exampleIdsCoursesArray =
      this.form.controls.exampleTagIdsCourses.value.sort()
    const exampleIdsSubscriptionsArray =
      this.form.controls.exampleTagIdsSubscriptions.value.sort()
    const exampleIdsRentalsArray =
      this.form.controls.exampleTagIdsRentals.value.sort()

    this.onSubmit.emit({
      primary_tag_filter_config: JSON.stringify(this.form.value.configSets),
      primary_tag_filter_config_courses: JSON.stringify(
        this.form.value.configSetsCourses
      ),
      primary_tag_filter_config_subscriptions: JSON.stringify(
        this.form.value.configSetsSubscriptions
      ),
      primary_tag_filter_config_rentals: JSON.stringify(
        this.form.value.configSetsRentals
      ),
      tagfilter_example_ids: JSON.stringify(exampleIdsArray),
      tagfilter_example_ids_courses: JSON.stringify(exampleIdsCoursesArray),
      tagfilter_example_ids_subscriptions: JSON.stringify(
        exampleIdsSubscriptionsArray
      ),
      tagfilter_example_ids_rentals: JSON.stringify(exampleIdsRentalsArray)
    })
  }

  markGroupDirty(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      switch (formGroup.get(key).constructor.name) {
        case 'FormGroup':
          this.markGroupDirty(formGroup.get(key) as UntypedFormGroup)
          break
        case 'FormArray':
          this.markArrayDirty(formGroup.get(key) as UntypedFormArray)
          break
        case 'FormControl':
          this.markControlDirty(formGroup.get(key) as UntypedFormControl)
          break
      }
    })
  }
  markArrayDirty(formArray: UntypedFormArray) {
    formArray.controls.forEach((control) => {
      switch (control.constructor.name) {
        case 'FormGroup':
          this.markGroupDirty(control as UntypedFormGroup)
          break
        case 'FormArray':
          this.markArrayDirty(control as UntypedFormArray)
          break
        case 'FormControl':
          this.markControlDirty(control as UntypedFormControl)
          break
      }
    })
  }
  markControlDirty(formControl: UntypedFormControl) {
    formControl.markAsDirty()
  }
  public sortTags(tags) {
    let language: string
    this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(take(1))
      .subscribe((languag) => {
        language = languag
      })
    return tags.sort((a, b) =>
      a['description_' + language] < b['description_' + language] ? -1 : 1
    )
  }
}
